import {Component} from '@angular/core';
import {FormBuilder, FormControl, ReactiveFormsModule} from '@angular/forms';
import {EmMarkdownPipe, validators} from '@emma-labs/shared/angular';
import {
  buildModificationNoticeBreakdown,
  ModificationNoticeBreakdown,
  SupportedLanguageCode,
  TextTranslations,
} from '@emma-labs/shared/typescript';
import {UntilDestroy} from '@ngneat/until-destroy';
import {ButtonModule} from 'primeng/button';
import {DialogModule} from 'primeng/dialog';
import {DynamicDialogConfig, DynamicDialogRef} from 'primeng/dynamicdialog';
import {InputTextareaModule} from 'primeng/inputtextarea';
import {SurchargeSummary} from '../../../entities/underwriting-page-object/surcharge-summary';
import {ProfileDecisionPopupHeaderComponent} from '../profile-decision-popup-header.component';
import {hasNoInputPrompts, INSERT_TEXT_EN, INSERT_TEXT_FR} from '../utils';

@UntilDestroy()
@Component({
  selector: 'em-profile-approval-popup',
  templateUrl: './profile-approval-popup.component.html',
  imports: [
    DialogModule,
    ReactiveFormsModule,
    InputTextareaModule,
    ButtonModule,
    EmMarkdownPipe,
    ProfileDecisionPopupHeaderComponent,
  ],
})
export class ProfileApprovalPopupComponent {
  preInputText: TextTranslations = {fr: '', en: ''};
  postInputText: TextTranslations = {fr: '', en: ''};
  languageCode: SupportedLanguageCode;
  surchargeSummary: SurchargeSummary;

  readonly form?: FormControl<string | null>;

  constructor(
    readonly fb: FormBuilder,
    readonly config: DynamicDialogConfig,
    private readonly ref: DynamicDialogRef,
  ) {
    this.surchargeSummary = config.data.surchargeSummary;
    const withCondition = config.data.withCondition;

    this.languageCode = this.surchargeSummary.languageCode;
    this.setParagraphs(this.surchargeSummary);
    if (withCondition) {
      this.form = fb.control<string | null>(
        this.surchargeSummary.languageCode === 'fr' ? INSERT_TEXT_FR : INSERT_TEXT_EN,
        [validators.notEmpty, hasNoInputPrompts],
      );
    }
  }

  submit(): void {
    const italicizedFormValue = this.form?.value
      ? this.form.value
          .split('\n')
          .map((line) => line.trim())
          .map((trimmedLine) => (trimmedLine ? `*${trimmedLine}*` : trimmedLine))
          .join('\n') + '\n\n'
      : undefined;

    this.ref.close(
      buildModificationNoticeBreakdown(
        new Date(),
        this.surchargeSummary.clientFullName,
        this.surchargeSummary.policyNumber,
        this.surchargeSummary.proposalCompletionDate,
        this.surchargeSummary.protectionSummary,
        this.surchargeSummary.protection,
        this.surchargeSummary.surchargedProtectionSummary,
        this.surchargeSummary.surchargedProtection,
        italicizedFormValue,
      ),
    );
  }

  private setParagraphs(surchargeSummary: SurchargeSummary): void {
    const modificationNoticeTexts: ModificationNoticeBreakdown = buildModificationNoticeBreakdown(
      new Date(),
      surchargeSummary.clientFullName,
      surchargeSummary.policyNumber,
      surchargeSummary.proposalCompletionDate,
      surchargeSummary.protectionSummary,
      surchargeSummary.protection,
      surchargeSummary.surchargedProtectionSummary,
      surchargeSummary.surchargedProtection,
    );

    this.preInputText = modificationNoticeTexts.preConditionText;
    this.postInputText = modificationNoticeTexts.postConditionText;

    return;
  }
}
